import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FileWithPath } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import {
  threadHistoryState,
  useAuth,
  useChatData,
  useChatInteract,
  useChatMessages,
  useConfig,
  currentThreadIdState,
  sessionIdState,
  useApi,
  IThread,
  IFileRef,
  IStep
} from '@chainlit/react-client';

import Alert from '@/components/Alert';
import { TaskList } from '@/components/Tasklist';
import { Translator } from 'components/i18n';
import { useTranslation } from 'components/i18n/Translator';

import { useUpload } from '@/hooks/useUpload';
import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';
import { Badge } from '@/components/ui/badge';

import { documentContextEnabledState, snowAttachmentFlowState, uploadedDocumentNamesState } from 'state/chat';

import { uploadDocuments } from '@/services/documentUpload';
import { isDocumentContextEnabledFromThread } from '@/utils/documentContext';
import { useFileUploadSettings } from '@/hooks/useFileUploadSettings';
import { type FileUploadSettings } from '@/state/uiSettings';
import { validateMultipleFiles, getFormattedErrorMessage } from '@/utils/fileValidation';

import { ErrorBoundary } from '../ErrorBoundary';
import ChatFooter from './Footer';
import MessagesContainer from './MessagesContainer';
import ScrollContainer from './ScrollContainer';
import WelcomeScreen from './WelcomeScreen';

const Chat = () => {
  const { user } = useAuth();
  const { config } = useConfig();
  const { sendMessage, uploadFile } = useChatInteract();
  const setThreads = useSetRecoilState(threadHistoryState);
  const setDocumentContextEnabled = useSetRecoilState(
    documentContextEnabledState
  );
  const setUploadedDocumentNames = useSetRecoilState(uploadedDocumentNamesState);
  const setCurrentThreadId = useSetRecoilState(currentThreadIdState);
  const sessionId = useRecoilValue(sessionIdState);

  const autoScrollRef = useRef(true);
  const { error, disabled, callFn } = useChatData();
  const navigate = useNavigate();

  // File upload settings from global UI settings
  const uploadSettings = useFileUploadSettings();

  // Update file upload MIME types to use standard format following Mozilla's guidelines: @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#unique_file_type_specifiers
  // Instead of using '*/*' which may cause MIME type warnings, we specify exact MIME type categories:
  // - 'application/*' - for general files
  // - 'audio/*' - for audio files
  // - 'image/*' - for image files
  // - 'text/*' - for text files
  // - 'video/*' - for video files
  // This provides better type safety and clearer file type expectations.
  const fileSpec = useMemo(
    () => ({
      max_size_mb:
        config?.features?.spontaneous_file_upload?.max_size_mb || 500,
      max_files: config?.features?.spontaneous_file_upload?.max_files || 20,
      accept: config?.features?.spontaneous_file_upload?.accept || {
        'application/*': [], // All application files
        'audio/*': [], // All audio files
        'image/*': [], // All image files
        'text/*': [], // All text files
        'video/*': [] // All video files
      }
    }),
    [config]
  );

  const { t } = useTranslation();
  const { threadId, messages } = useChatMessages();
  const layoutMaxWidth = useLayoutMaxWidth();
  const snowAttachmentFlowActive = useRecoilValue(snowAttachmentFlowState);

  useEffect(() => {
    console.debug('[Chat] Snow attachment flow state changed', {
      snowAttachmentFlowActive
    });
  }, [snowAttachmentFlowActive]);

  const { data: thread } = useApi<IThread>(threadId ? `/project/thread/${threadId}` : null, {
    revalidateOnFocus: false
  });
  const docContextEnabledFromThread = isDocumentContextEnabledFromThread(thread);
  const documentContextEnabled = useRecoilValue(documentContextEnabledState);

  useEffect(() => {
    if (callFn) {
      const event = new CustomEvent('chainlit-call-fn', {
        detail: callFn
      });
      window.dispatchEvent(event);
    }
  }, [callFn]);

  // Session state management: Clear document names when document context is disabled
  // Note: This manages session-level state for newly uploaded documents
  // Thread-level persistence is now handled in MessageComposer via thread metadata
  useEffect(() => {
    if (!documentContextEnabled && !docContextEnabledFromThread) {
      setUploadedDocumentNames([]);
    }
  }, [documentContextEnabled, docContextEnabledFromThread, setUploadedDocumentNames]);

  // Clear session document names when thread changes (new conversation)
  useEffect(() => {
    setUploadedDocumentNames([]);
  }, [threadId, setUploadedDocumentNames]);

  const onFileUpload = useCallback(
    async (files: FileWithPath[]) => {
      if (!files.length) return;

      const validationResult = validateMultipleFiles(
        files as File[],
        uploadSettings.maxFiles,
        uploadSettings.maxSizeMB,
        uploadSettings.allowedExtensions
      );

      if (!validationResult.valid) {
        toast.error(getFormattedErrorMessage(validationResult));
        return;
      }

      let currentThreadId = threadId ?? undefined;
      let ragConversationIdHint: string | undefined;

      // Check latest message metadata directly to avoid race condition with Recoil state
      // The Recoil atom might not have updated yet when this callback runs
      let effectiveSnowFlowActive = snowAttachmentFlowActive;
      if (messages && messages.length > 0) {
        // Check last few messages for flow reset hints
        const recentMessages = messages.slice(-5).reverse();

        
        // Helper to check message and nested steps recursively
        const checkMessageForReset = (step: any): boolean => {
          let metadata = step.metadata;
          
          // Handle both string and object metadata
          if (typeof metadata === 'string') {
            try {
              metadata = JSON.parse(metadata);
            } catch {
              // Continue to check nested steps
            }
          }
          
          if (metadata && typeof metadata === 'object') {
            const uiHints = (metadata as any).ui_hints;
            if (uiHints?.snow_attachment_flow_after === false) {
              return true;
            }
          }
          
          // Check nested steps
          if (step.steps && Array.isArray(step.steps)) {
            for (const nestedStep of step.steps) {
              if (checkMessageForReset(nestedStep)) {
                return true;
              }
            }
          }
          
          return false;
        };
        
        for (const msg of recentMessages) {
          if (checkMessageForReset(msg)) {
            effectiveSnowFlowActive = false;
            break;
          }
        }
      }

      if (!currentThreadId && !sessionId) {
        toast.error('Unable to determine conversation. Try refreshing and retry.');
        return;
      }

      if (effectiveSnowFlowActive) {
        for (const file of files) {

          try {
            const { promise } = uploadFile(file as File, () => {});

            toast.promise<IFileRef>(promise, {
              loading: `Uploading ${file.name}…`,
              success: () => `${file.name} ready to attach`,
              error: (error) => {
                const errorMsg = error instanceof Error ? error.message : String(error);
                return `${t('chat.fileUpload.errors.failed')} ${file.name}: ${errorMsg}`;
              }
            });

            const fileRef = await promise;

            const userMessageId = uuidv4();
            sendMessage(
              {
                threadId: currentThreadId,
                type: 'user_message',
                id: userMessageId,
                name: user?.identifier || 'User',
                output: file.name,
                createdAt: new Date().toISOString(),
                metadata: {
                  location: window.location.href,
                  snowAttachmentUpload: {
                    fileName: file.name,
                    status: 'uploaded'
                  }
                  // Removed: autoGenerated and skipIntentClassification
                  // Backend needs to process this to upload to ServiceNow
                }
              },
              [fileRef]
            );
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            const errorMessageId = uuidv4();
            sendMessage({
              threadId: currentThreadId,
              type: 'assistant_message',
              id: errorMessageId,
              name: 'assistant',
              output: `Failed to upload ${file.name}`,
              isError: true,
              createdAt: new Date().toISOString(),
              metadata: {
                snowAttachmentUpload: {
                  fileName: file.name,
                  status: 'failed',
                  error: errorMessage
                },
                skipIntentClassification: true
              }
            });
          }
        }
        return;
      }

      // Send a user message for each file upfront
      for (const file of files) {
        const userMessageId = uuidv4();
        sendMessage({
          threadId: currentThreadId,
          type: 'user_message',
          id: userMessageId,
          name: user?.identifier || 'User',
          output: file.name,
          createdAt: new Date().toISOString(),
          metadata: {
            location: window.location.href,
            documentUpload: { fileName: file.name, autoGenerated: true, status: 'uploading' },
            skipIntentClassification: true
          }
        });
      }

      // Single request for all files — avoids creating multiple conversation IDs
      const toastLabel = files.length === 1
        ? `Uploading ${files[0].name}…`
        : `Uploading ${files.length} files…`;

      toast.promise(
        (async () => {
          const result = await uploadDocuments(files, {
            conversationId: currentThreadId,
            sessionId,
            maxSizeMB: uploadSettings.maxSizeMB,
            allowedExtensions: uploadSettings.allowedExtensions,
            isSnowbotAttachmentFlow: false,
            metadata: {
              ...(ragConversationIdHint ? { ragConversationId: ragConversationIdHint } : {})
            }
          });

          const nextThreadId = result?.thread_id ?? currentThreadId;

          if (result?.conversation_id) {
            ragConversationIdHint = result.conversation_id;
          }

          if (nextThreadId && nextThreadId !== currentThreadId) {
            currentThreadId = nextThreadId;
            setCurrentThreadId(nextThreadId);
            setThreads((prev) => ({ ...prev, currentThreadId: nextThreadId }));
            if (window.location.pathname === '/') {
              navigate(`/thread/${nextThreadId}`);
            }
          }

          // Per-file success/error assistant messages
          const fileEntries = result?.files ?? files.map(f => ({ filename: f.name, success: true, status: result?.status }));
          let successCount = 0;
          for (const entry of fileEntries) {
            const fName = entry.filename ?? 'file';
            const msgId = uuidv4();
            if (entry.success !== false) {
              successCount++;
              sendMessage({
                threadId: nextThreadId,
                type: 'assistant_message',
                id: msgId,
                name: 'assistant',
                output: `${fName} uploaded successfully`,
                createdAt: new Date().toISOString(),
                metadata: {
                  documentUpload: { fileName: fName, status: entry.status || 'success', autoGenerated: true },
                  ...(ragConversationIdHint ? { ragConversationId: ragConversationIdHint } : {})
                }
              });
              setUploadedDocumentNames(prev => prev.includes(fName) ? prev : [...prev, fName]);
            } else {
              sendMessage({
                threadId: nextThreadId,
                type: 'assistant_message',
                id: msgId,
                name: 'assistant',
                output: `Failed to upload ${fName}`,
                isError: true,
                createdAt: new Date().toISOString(),
                metadata: {
                  documentUpload: { fileName: fName, status: 'failed', autoGenerated: true, error: entry.error ?? undefined }
                }
              });
            }
          }

          setDocumentContextEnabled(true);
          return files.length === 1
            ? `${files[0].name} uploaded successfully`
            : `${successCount} of ${files.length} files uploaded successfully`;
        })(),
        {
          loading: toastLabel,
          success: (message) => message,
          error: (error) => {
            const errorMsg = error instanceof Error ? error.message : String(error);
            for (const file of files) {
              sendMessage({
                threadId: currentThreadId,
                type: 'assistant_message',
                id: uuidv4(),
                name: 'assistant',
                output: `Failed to upload ${file.name}`,
                isError: true,
                createdAt: new Date().toISOString(),
                metadata: {
                  documentUpload: { fileName: file.name, status: 'failed', autoGenerated: true, error: errorMsg }
                }
              });
            }
            return `${t('chat.fileUpload.errors.failed')} ${errorMsg}`;
          }
        }
      );
    },
    [messages, navigate, sendMessage, sessionId, setCurrentThreadId, setDocumentContextEnabled, setThreads, snowAttachmentFlowActive, t, threadId, uploadFile, user]
  );

  const onFileUploadError = useCallback(
    (error: string) => {
      console.error('[Chat::onFileUploadError] File upload validation error', {
        error,
        timestamp: new Date().toISOString()
      });
      toast.error(error);
    },
    []
  );
  const ragUploadSpec = useMemo(() => {
    const maxFiles = fileSpec.max_files
      ? Math.min(fileSpec.max_files, 5)
      : 5;
    const maxSize = fileSpec.max_size_mb
      ? Math.min(fileSpec.max_size_mb, 50)
      : 50;
    return {
      ...fileSpec,
      accept: {
        'application/pdf': ['.pdf'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
          '.docx'
        ],
        'text/plain': ['.txt'],
        'text/csv': ['.csv'],
        'image/jpeg': ['.jpeg', '.jpg'],
        'image/png': ['.png']
      },
      max_files: maxFiles,
      max_size_mb: maxSize
    };
  }, [fileSpec]);

  const upload = useUpload({
    spec: ragUploadSpec,
    onResolved: onFileUpload,
    onError: onFileUploadError,
    options: { noClick: true }
  });

  useEffect(() => {
    const currentPage = new URL(window.location.href);
    if (
      user &&
      config?.dataPersistence &&
      threadId &&
      currentPage.pathname === '/'
    ) {
      navigate(`/thread/${threadId}`);
    }

    setThreads((prev) => ({
      ...prev,
      currentThreadId: threadId
    }));
  }, [config?.dataPersistence, navigate, setThreads, threadId, user]);

  const enableAttachments =
    !disabled && config?.features?.spontaneous_file_upload?.enabled;
  return (
    <div
      {...(enableAttachments
        ? upload.getRootProps({ className: 'dropzone' })
        : {})}
      // Disable the onFocus and onBlur events in react-dropzone to avoid interfering with child trigger events
      onBlur={undefined}
      onFocus={undefined}
      className="flex w-full h-full flex-col relative"
    >
      {enableAttachments ? (
        <input id="#upload-drop-input" {...upload.getInputProps()} />
      ) : null}

      {error ? (
        <div className="w-full mx-auto my-2">
          <Alert className="mx-2" id="session-error" variant="error">
            <Translator path="common.status.error.serverConnection" />
          </Alert>
        </div>
      ) : null}
      <ErrorBoundary>
        <ScrollContainer
          autoScrollUserMessage={config?.features?.user_message_autoscroll}
          autoScrollRef={autoScrollRef}
        >          <div
            className="flex flex-col mx-auto w-full flex-grow p-2 sm:p-4"
            style={{
              maxWidth: layoutMaxWidth
            }}
          >            <TaskList isMobile={true} />
            <WelcomeScreen />
            <MessagesContainer navigate={navigate} />
          </div>
        </ScrollContainer>        <div
          className="flex flex-col mx-auto w-full p-2 sm:p-4 pt-0"
          style={{
            maxWidth: layoutMaxWidth
          }}
        >
          <ChatFooter
            showIfEmptyThread={true}
            fileSpec={fileSpec}
            onFileUpload={onFileUpload}
            onFileUploadError={onFileUploadError}
            autoScrollRef={autoScrollRef}
            thread={thread}
          />
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default Chat;
